import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Select, {type SelectChangeEvent} from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {alpha, useTheme} from "@mui/material/styles";
import * as React from "react";
import {useTranslation} from "react-i18next";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from "recharts";
import {chartPalette} from "../../components/chartColors";
import {InsightsLegend, LegendEntry} from "../../kit/InsightsLegend";
import {InsightsColumn, InsightsTable} from "../../kit/InsightsTable";
import {SeriesCard} from "../../kit/SeriesCard";
import {InsightsTooltipPanel, TooltipRow, TooltipTitle} from "../../kit/InsightsTooltip";
import {CHART_BAR_RADIUS, CHART_DRAW_DURATION, CHART_HEIGHT} from "../../kit/insightsChart";
import type {InsightsLoading} from "../../kit/insightsLoading";
import type {CrmSeasonalityV4} from "../../models/crm-v4";
import {currentCalendarMonthKey, currentMonthBarColor} from "../base/InsightCurrentMonthHighlight";

interface SeasonalityCardProps {
    data?: CrmSeasonalityV4;
    loading?: InsightsLoading;
}

const MONTH_LABELS: Record<string, string> = {
    JAN: "Jan", FEB: "Feb", MAR: "Mar", APR: "Apr", MAY: "May", JUN: "Jun",
    JUL: "Jul", AUG: "Aug", SEP: "Sep", OCT: "Oct", NOV: "Nov", DEC: "Dec",
};

/** One row per calendar month, one key per year, which is the shape a grouped bar chart reads. */
interface MonthRow {
    month: string;
    label: string;
    [year: string]: string | number | null;
}

interface MonthTickProps {
    x?: number;
    y?: number;
    payload?: {value?: string};
    fill: string;
    highlighted?: string;
}

/** The month we are standing in is incomplete, so its column is marked rather than read as a full month. */
function MonthTick({x, y, payload, fill, highlighted}: MonthTickProps) {
    const value = payload?.value ?? "";
    const isCurrent = highlighted != null && value === highlighted;

    return (
        <text
            x={x}
            y={y}
            dy={12}
            textAnchor={"middle"}
            fill={fill}
            fontSize={12}
            fontWeight={isCurrent ? 700 : 400}
            textDecoration={isCurrent ? "underline" : undefined}
        >
            {value}
        </text>
    );
}

interface SeasonalityTooltipProps {
    active?: boolean;
    label?: string;
    payload?: Array<{dataKey?: string | number; value?: number | null; color?: string}>;
    format: (value: number) => string;
}

function SeasonalityTooltip({active, label, payload, format}: SeasonalityTooltipProps) {
    const rows = (payload ?? []).filter(entry => entry.value != null);

    if (!active || rows.length === 0) {
        return null;
    }

    return (
        <InsightsTooltipPanel>
            <TooltipTitle title={label ?? ""}/>

            {rows.map(entry => (
                <TooltipRow
                    key={String(entry.dataKey)}
                    swatch={entry.color}
                    label={String(entry.dataKey)}
                    value={format(Number(entry.value))}
                />
            ))}
        </InsightsTooltipPanel>
    );
}

export function SeasonalityCard({data, loading}: SeasonalityCardProps) {
    const {t, i18n} = useTranslation();
    const theme = useTheme();
    const [excludedYears, setExcludedYears] = React.useState<number[]>([]);

    const locale = i18n.resolvedLanguage || i18n.language;
    const months = React.useMemo(() => data?.months ?? [], [data?.months]);
    const availableSeries = React.useMemo(() => data?.series ?? [], [data?.series]);
    const availableYears = new Set(availableSeries.map(series => series.year));
    const selectedSeries = availableSeries.filter(series => !excludedYears.includes(series.year));
    const selectedYears = selectedSeries.map(series => series.year);
    const hasData = availableSeries.some(series => series.values.some(value => value > 0));

    const palette = chartPalette(theme);
    const colorByYear = new Map(availableSeries.map((series, index) => [
        series.year,
        palette[index % palette.length],
    ]));

    const countFormatter = new Intl.NumberFormat(locale, {maximumFractionDigits: 0});
    const compactFormatter = new Intl.NumberFormat(locale, {notation: "compact", maximumFractionDigits: 1});
    const formatNights = (value: number) => `${countFormatter.format(value)} ${t("nights")}`;

    const currentMonthKey = currentCalendarMonthKey();
    const currentMonthLabel = months.includes(currentMonthKey)
        ? t(MONTH_LABELS[currentMonthKey] ?? currentMonthKey)
        : undefined;

    const rows = React.useMemo<MonthRow[]>(() => months.map((month, index) => {
        const row: MonthRow = {month, label: t(MONTH_LABELS[month] ?? month)};

        selectedSeries.forEach(series => {
            row[String(series.year)] = series.values[index] ?? null;
        });

        return row;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [months, selectedYears.join(","), availableSeries, t]);

    // Re-running the draw on every hover would be noise; it plays once per change of what is charted.
    const [animate, setAnimate] = React.useState(true);

    React.useEffect(() => {
        setAnimate(true);
        const timer = window.setTimeout(() => setAnimate(false), CHART_DRAW_DURATION + 400);

        return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedYears.join(",")]);

    const handleSelectionChange = (event: SelectChangeEvent<number[]>) => {
        const nextSelected = typeof event.target.value === "string"
            ? event.target.value.split(",").map(Number)
            : event.target.value;

        setExcludedYears(previous => [
            ...previous.filter(year => !availableYears.has(year)),
            ...availableSeries.filter(series => !nextSelected.includes(series.year)).map(series => series.year),
        ]);
    };

    const renderSelection = (years: number[]) => {
        if (years.length === availableSeries.length) return t("All years");
        if (years.length === 0) return t("No years selected");
        if (years.length === 1) return `${years[0]}`;
        return t("{{count}} years", {count: years.length});
    };

    const yearsPicker = (
        <FormControl size={"small"} sx={{minWidth: 180}}>
            <InputLabel id={"insights-seasonality-years-label"}>{t("Years")}</InputLabel>
            <Select<number[]>
                multiple
                labelId={"insights-seasonality-years-label"}
                label={t("Years")}
                value={selectedYears}
                onChange={handleSelectionChange}
                renderValue={renderSelection}
                disabled={loading != null || !hasData}
                sx={{height: 36, "& .MuiSelect-select": {py: 0.75}}}
                MenuProps={{PaperProps: {sx: {maxHeight: 360}}}}
            >
                {availableSeries.map(series => (
                    <MenuItem key={series.year} value={series.year}>
                        <Checkbox checked={selectedYears.includes(series.year)}/>
                        <ListItemText primary={series.year}/>
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );

    const placeholder = (text: string) => (
        <Stack alignItems={"center"} justifyContent={"center"} sx={{height: CHART_HEIGHT, px: 3}}>
            <Typography variant={"body2"} color={"text.secondary"} textAlign={"center"}>
                {text}
            </Typography>
        </Stack>
    );

    const columns: InsightsColumn<MonthRow>[] = [
        {
            key: "month",
            label: t("Month"),
            kind: "label",
            cell: row => row.label,
        },
        ...selectedSeries.map(series => ({
            key: String(series.year),
            label: `${series.year}`,
            kind: "value" as const,
            cell: (row: MonthRow) => {
                const value = row[String(series.year)];

                return value == null ? "—" : formatNights(Number(value));
            },
        })),
    ];

    const empty = !hasData ? placeholder(t("No room stays on record yet"))
        : selectedSeries.length === 0 ? placeholder(t("Select at least one year"))
            : undefined;

    return (
        <SeriesCard
            title={t("Seasonality")}
            hint={t("Compare room nights for the same calendar month across selected years")}
            loading={loading}
            actions={hasData ? yearsPicker : undefined}
            empty={empty}
            legend={(
                <InsightsLegend>
                    {selectedSeries.map(series => (
                        <LegendEntry
                            key={series.year}
                            shape={"bar"}
                            color={colorByYear.get(series.year) ?? theme.palette.text.secondary}
                            label={`${series.year}`}
                        />
                    ))}
                </InsightsLegend>
            )}
            table={<InsightsTable label={t("Seasonality")} columns={columns} rows={rows} rowKey={row => row.month}/>}
            chart={(
                        <Box sx={{width: "100%", minWidth: 0, height: CHART_HEIGHT}}>
                            <ResponsiveContainer width={"100%"} height={"100%"} minWidth={0}>
                                <BarChart data={rows} margin={{top: 8, right: 8, bottom: 0, left: 0}} barGap={1}>
                                    <CartesianGrid vertical={false} stroke={theme.palette.divider} strokeOpacity={0.8}/>

                                    <XAxis
                                        dataKey={"label"}
                                        axisLine={false}
                                        tickLine={false}
                                        tickMargin={12}
                                        height={34}
                                        interval={0}
                                        tick={<MonthTick fill={theme.palette.text.secondary} highlighted={currentMonthLabel}/>}
                                    />

                                    <YAxis
                                        width={48}
                                        axisLine={false}
                                        tickLine={false}
                                        tickMargin={8}
                                        domain={[0, "auto"]}
                                        tick={{fill: theme.palette.text.secondary, fontSize: 11}}
                                        tickFormatter={value => compactFormatter.format(value)}
                                    />

                                    <RechartsTooltip
                                        isAnimationActive={false}
                                        offset={14}
                                        cursor={{fill: alpha(theme.palette.text.primary, 0.05)}}
                                        wrapperStyle={{pointerEvents: "none", transition: "none", outline: "none", zIndex: 10}}
                                        content={<SeasonalityTooltip format={formatNights}/>}
                                    />

                                    {selectedSeries.map(series => {
                                        const color = colorByYear.get(series.year) ?? theme.palette.text.secondary;

                                        return (
                                            <Bar
                                                key={series.year}
                                                dataKey={String(series.year)}
                                                fill={color}
                                                radius={[CHART_BAR_RADIUS, CHART_BAR_RADIUS, 0, 0]}
                                                maxBarSize={18}
                                                isAnimationActive={animate}
                                                animationDuration={CHART_DRAW_DURATION}
                                                animationEasing={"ease-out"}
                                            >
                                                {rows.map(row => (
                                                    <Cell
                                                        key={row.month}
                                                        fill={row.month === currentMonthKey ? currentMonthBarColor(color) : color}
                                                    />
                                                ))}
                                            </Bar>
                                        );
                                    })}
                                </BarChart>
                            </ResponsiveContainer>
                        </Box>
            )}
        />
    );
}

export default SeasonalityCard;
